import { Link } from '@tanstack/react-router';
import { type AlertNotifiedMessage, type Severity, severityName } from '@vision360/contracts';
import type { TFunction } from 'i18next';
import { Check, X } from 'lucide-react';
import {
  type FocusEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { canAck } from '@/features/alerts/alert-actions';
import { useAckAlert } from '@/features/alerts/api';
import { useAlertNotified } from '@/features/live/live-stream';
import { usePlatform } from '@/lib/platform/context';
import { cn } from '@/lib/utils';
import { SEVERITY_CLASS } from '@/widgets/_sdk/format';

/**
 * Step 32 (PRD VS-061, TechSpec §12.3): renders what `alert.notified` says was delivered —
 * a toast for `in-app`, a native notification through the platform for `desktop`. The API
 * decides; nothing here filters. One toast per alert: a new notification replaces it on top.
 */

export const MAX_TOASTS = 4;
export const DISMISS_MS = 10_000;
/** Matches `duration-150` on the card. */
const EXIT_MS = 150;

const RAIL: Record<Severity, string> = {
  0: 'bg-muted-foreground/40',
  1: 'bg-emerald-500',
  2: 'bg-amber-500',
  3: 'bg-red-500',
  4: 'bg-red-600',
};

interface Toast {
  key: number;
  message: AlertNotifiedMessage;
  leaving: boolean;
}

const reasonLabel = (t: TFunction<'notifications'>, { reason, alert }: AlertNotifiedMessage) =>
  t(`toast.reason.${reason}`, { count: alert.fireCount });

/** Runs `onDone` after `ms`, holding the time left while paused; `null` never fires. */
function useDismissTimer(ms: number | null, paused: boolean, onDone: () => void) {
  const left = useRef(ms ?? 0);
  useEffect(() => {
    if (ms === null || paused) return;
    const started = Date.now();
    const timer = setTimeout(onDone, left.current);
    return () => {
      clearTimeout(timer);
      left.current -= Date.now() - started;
    };
  }, [ms, paused, onDone]);
}

function ToastCard({ toast, close }: { toast: Toast; close: (key: number) => void }) {
  const { t } = useTranslation(['notifications', 'widgets']);
  const { alert } = toast.message;
  const ack = useAckAlert();
  const titleId = useId();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const dismiss = useCallback(() => close(toast.key), [close, toast.key]);
  // Critical stays until closed (VS-061); hover or focus holds the others.
  useDismissTimer(
    alert.severity === 4 || toast.leaving ? null : DISMISS_MS,
    hovered || focused,
    dismiss,
  );

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    // Keep the keyboard in the stack: the next toast, else the previous one.
    const neighbour =
      event.currentTarget.nextElementSibling ?? event.currentTarget.previousElementSibling;
    if (neighbour instanceof HTMLElement) neighbour.focus();
    dismiss();
  };
  const onBlur = (event: FocusEvent<HTMLElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
  };

  return (
    <article
      tabIndex={-1}
      aria-labelledby={titleId}
      data-severity={alert.severity}
      className={cn(
        'glass pointer-events-auto relative flex animate-rise overflow-hidden transition-opacity duration-150 ease-in focus-visible:outline-2',
        alert.severity === 4 && 'ring-1 ring-red-600/50',
        toast.leaving && 'opacity-0',
      )}
      onKeyDown={onKeyDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={onBlur}
    >
      <span aria-hidden="true" className={cn('w-1 shrink-0', RAIL[alert.severity])} />
      <div className="min-w-0 flex-1 space-y-1.5 py-3 pl-3">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'shrink-0 rounded-sm px-1.5 py-0.5 text-xs font-medium',
              SEVERITY_CLASS[alert.severity],
            )}
          >
            {t(`widgets:severity.${severityName(alert.severity)}`)}
          </span>
          <span className="truncate text-xs text-muted-foreground tabular-nums">
            {reasonLabel(t, toast.message)}
          </span>
        </div>
        <p id={titleId} className="line-clamp-2 text-sm leading-snug font-medium break-words">
          {alert.title}
        </p>
        {alert.ruleName && (
          <p className="truncate text-xs text-muted-foreground">{alert.ruleName}</p>
        )}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {canAck(alert) && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 pointer-coarse:h-11"
              disabled={ack.isPending}
              onClick={() => ack.mutate(alert.id, { onSuccess: dismiss })}
            >
              {ack.isPending ? <Spinner /> : <Check aria-hidden="true" className="h-4 w-4" />}
              {t('notifications:toast.ack')}
            </Button>
          )}
          <Button asChild size="sm" variant="ghost" className="h-8 pointer-coarse:h-11">
            <Link to="/alerts" onClick={dismiss}>
              {t('notifications:toast.view')}
            </Link>
          </Button>
        </div>
        <div aria-live="polite" className="text-xs text-destructive">
          {ack.isError && t('notifications:toast.ackFailed')}
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="m-1 h-8 w-8 shrink-0 p-0 text-muted-foreground pointer-coarse:h-11 pointer-coarse:w-11"
        aria-label={t('notifications:toast.close', { title: alert.title })}
        onClick={dismiss}
      >
        <X aria-hidden="true" className="h-4 w-4" />
      </Button>
    </article>
  );
}

export function Toaster() {
  const { t } = useTranslation('notifications');
  const platform = usePlatform();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [announcement, setAnnouncement] = useState({ polite: '', assertive: '' });
  const seq = useRef(0);

  const onNotified = useCallback(
    (message: AlertNotifiedMessage) => {
      const { alert, channels } = message;
      const reason = reasonLabel(t, message);
      if (channels.includes('desktop')) {
        // A native failure stays on the native side: the toast and the stream go on.
        void platform
          .notify?.({
            title: alert.title,
            body: alert.ruleName ? t('toast.nativeBody', { reason, rule: alert.ruleName }) : reason,
            severity: alert.severity,
          })
          ?.catch(() => {});
      }
      if (!channels.includes('in-app')) return;
      const text = t('toast.announce', { reason, title: alert.title });
      setAnnouncement(
        alert.severity === 4 ? { polite: '', assertive: text } : { polite: text, assertive: '' },
      );
      seq.current += 1;
      const toast = { key: seq.current, message, leaving: false };
      setToasts((current) =>
        [toast, ...current.filter((x) => x.message.alert.id !== alert.id)].slice(0, MAX_TOASTS),
      );
    },
    [platform, t],
  );
  useAlertNotified(onNotified);

  const close = useCallback((key: number) => {
    setToasts((current) => current.map((x) => (x.key === key ? { ...x, leaving: true } : x)));
    setTimeout(() => setToasts((current) => current.filter((x) => x.key !== key)), EXIT_MS);
  }, []);

  return (
    <>
      {/* Always mounted, so screen readers hear the insertion; critical interrupts. */}
      <div role="status" aria-live="polite" className="sr-only">
        {announcement.polite}
      </div>
      <div role="alert" aria-live="assertive" className="sr-only">
        {announcement.assertive}
      </div>
      {toasts.length > 0 && (
        <section
          aria-label={t('toast.region')}
          className="pointer-events-none fixed right-4 bottom-4 z-40 flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
        >
          {toasts.map((toast) => (
            <ToastCard key={toast.key} toast={toast} close={close} />
          ))}
        </section>
      )}
    </>
  );
}
